import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { FaShoppingCart, FaUser, FaBars, FaTimes, FaHome, FaStore, FaChevronDown, FaGem, FaCog, FaSignOutAlt, FaBoxOpen } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../api/axios';

const Navbar = () => {
  const { itemCount } = useCart();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [, setIsMobile] = useState(window.innerWidth < 1024);
  const [profileDropdown, setProfileDropdown] = useState(false);
  const [anniversary, setAnniversary] = useState(null);
  const location = useLocation();
  const hasLightBackground = scrolled || location.pathname !== '/';

  const handleLogout = () => {
    logout();
    navigate('/login');
    setProfileDropdown(false);
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    
    const handleResize = () => {
      const mobile = window.innerWidth < 1024;
      setIsMobile(mobile);
      if (!mobile) {
        setIsOpen(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  useEffect(() => { api.get('/settings').then(({ data }) => setAnniversary(data.settings)).catch(() => {}); }, []);

  useEffect(() => {
    setIsOpen(false);
    setProfileDropdown(false);
  }, [location]);

  const menuVariants = {
    open: { 
      opacity: 1, 
      height: "auto", 
      transition: { 
        staggerChildren: 0.1,
        when: "beforeChildren"
      } 
    },
    closed: { 
      opacity: 0, 
      height: 0,
      transition: { 
        staggerChildren: 0.05, 
        staggerDirection: -1,
        when: "afterChildren"
      } 
    }
  };

  const itemVariants = {
    open: { 
      y: 0, 
      opacity: 1, 
      transition: { 
        y: { stiffness: 1000, velocity: -100 } 
      } 
    },
    closed: { 
      y: 50, 
      opacity: 0, 
      transition: { 
        y: { stiffness: 1000 } 
      } 
    }
  };

  return (
    <nav className={`fixed w-full z-50 transition-all duration-500 border-b ${hasLightBackground ? 'py-2.5 bg-white/95 backdrop-blur-xl shadow-sm border-[#0C4B45]/10' : 'py-3.5 bg-[#0A2E38]/98 backdrop-blur-xl shadow-lg border-white/5'}`}>
      <div className="container max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex justify-between items-center">
          
          {/* Logo con aniversario destacado */}
          <Link to="/" className="flex items-center space-x-2 sm:space-x-3 group relative rounded-xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[#83F4E9] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0C4B45]">
            <motion.div 
              className="relative"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.3 }}
            >
              {anniversary?.anniversaryLogoUrl ? <img src={anniversary.anniversaryLogoUrl} alt={anniversary.anniversaryAlt || 'Aniversario'} className="absolute -top-2 -right-3 z-20 h-12 w-12 object-contain drop-shadow-lg sm:h-14 sm:w-14" /> : <motion.div
                className="absolute -top-2 -right-3 z-20 flex h-11 w-11 items-center justify-center rounded-full border border-white/30 bg-gradient-to-br from-[#F2A9FD] to-[#662D8F] shadow-lg sm:h-12 sm:w-12"
                animate={{ rotate: [0, 5, 0, -5, 0] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
              >
                <div className="text-center">
                  <span className="block text-base font-bold leading-none text-white sm:text-lg">17</span>
                  <span className="block text-[9px] font-light italic text-white">años</span>
                  <FaGem className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 text-yellow-300 text-xs animate-pulse" />
                </div>
              </motion.div>}

              <div className="relative z-10 overflow-hidden rounded-xl bg-[#662D8F] p-2.5 shadow-lg transition-all duration-300 group-hover:shadow-md">
                <div className="absolute inset-0 opacity-20 bg-gradient-to-r from-[#F2A9FD] to-transparent" />
                <div className="relative z-10 flex h-8 w-8 items-center justify-center sm:h-9 sm:w-9">
                  <FaBoxOpen className="h-5 w-5 text-white" />
                </div>
              </div>
            </motion.div>
            
            <div className="flex flex-col leading-none">
              <span className={`relative inline-flex items-baseline text-2xl font-serif font-black tracking-[-0.055em] drop-shadow-sm sm:text-3xl ${hasLightBackground ? 'text-[#0C4B45]' : 'text-white'}`}>
                <span className="relative z-10 tracking-[-0.1em]">PC</span>
                <span className="relative z-10 ml-1 font-serif italic font-bold tracking-[-0.07em] text-[#F2A9FD] transition-all duration-300 group-hover:tracking-[-0.03em]">Vasquez</span>
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#83F4E9] transition-all duration-500 group-hover:w-full" />
              </span>
              <span className={`mt-1 text-[9px] sm:text-[10px] font-semibold tracking-[0.2em] ${hasLightBackground ? 'text-[#0C4B45]/70' : 'text-[#83F4E9]'} uppercase hidden xs:block`}>Innovación Tecnológica</span>
            </div>
          </Link>

          {/* Menú escritorio */}
          <div className="hidden lg:flex items-center space-x-1 xl:space-x-2">
            <NavLink to="/" active={location.pathname === '/'} scrolled={hasLightBackground}>
              <FaHome className="mr-1 sm:mr-2" /> Inicio
            </NavLink>

            <NavLink to="/products" active={location.pathname.startsWith('/products') || location.pathname.startsWith('/product/')} scrolled={hasLightBackground}>
              <FaStore className="mr-1 sm:mr-2" /> Productos
            </NavLink>

            <CartLink itemCount={itemCount} scrolled={hasLightBackground} />

            {user ? (
              <div className="relative">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setProfileDropdown(!profileDropdown)}
                  aria-expanded={profileDropdown}
                  aria-haspopup="menu"
                  className={`flex items-center px-3 py-2.5 rounded-xl font-medium ${hasLightBackground ? 'bg-gradient-to-r from-[#662D8F] to-[#512577] text-white' : 'bg-white/10 text-white backdrop-blur-sm'} shadow-md`}
                >
                  <FaUser className="mr-2" />
                  <span className="text-sm">{user.name?.split(' ')[0] || 'Cuenta'}</span>
                  <FaChevronDown className={`ml-2 text-xs transition-transform ${profileDropdown ? 'rotate-180' : ''}`} />
                </motion.button>

                <AnimatePresence>
                  {profileDropdown && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-3 w-52 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-50"
                    >
                      <div className="p-2">
                        <Link
                          to="/profile"
                          className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                          onClick={() => setProfileDropdown(false)}
                        >
                          <FaUser className="mr-3 text-[#662D8F]" />
                          Mi Perfil
                        </Link>
                       
                        <button
                          onClick={handleLogout}
                          className="flex items-center w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <FaSignOutAlt className="mr-3" />
                          Cerrar Sesión
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <NavLink to="/login" active={location.pathname === '/login'} scrolled={hasLightBackground}>
                <FaUser className="mr-1 sm:mr-2" /> Ingresar
              </NavLink>
            )}
          </div>

          {/* Iconos de carrito y usuario en móvil */}
          <div className="flex lg:hidden items-center space-x-2 sm:space-x-3">
            <CartLink itemCount={itemCount} scrolled={hasLightBackground} isMobile={true} />
            
            {user ? (
              <Link 
                to="/profile" 
                className={`p-2.5 rounded-xl ${hasLightBackground ? 'bg-[#662D8F] text-white hover:bg-[#512577]' : 'bg-white/20 text-white backdrop-blur-sm hover:bg-white/30'} shadow-md transition-colors`}
              >
                <FaUser className="text-lg" />
              </Link>
            ) : (
              <Link 
                to="/login" 
                className={`p-2.5 rounded-xl ${hasLightBackground ? 'bg-[#662D8F] text-white hover:bg-[#512577]' : 'bg-white/20 text-white backdrop-blur-sm hover:bg-white/30'} shadow-md transition-colors`}
              >
                <FaUser className="text-lg" />
              </Link>
            )}

            {/* Botón de menú móvil */}
            <motion.button
              onClick={() => setIsOpen(!isOpen)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className={`p-2.5 rounded-xl ${hasLightBackground ? 'bg-[#662D8F] text-white hover:bg-[#512577]' : 'bg-white/20 text-white backdrop-blur-sm hover:bg-white/30'} shadow-md transition-colors`}
              aria-label="Abrir menú"
            >
              {isOpen ? <FaTimes className="text-lg" /> : <FaBars className="text-lg" />}
            </motion.button>
          </div>
        </div>

        {/* Menú móvil desplegable */}
        <motion.div
          className="lg:hidden overflow-hidden"
          initial="closed"
          animate={isOpen ? "open" : "closed"}
          variants={menuVariants}
          style={{ 
            pointerEvents: isOpen ? 'auto' : 'none',
          }}
        >
          <motion.div 
            className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl p-3 mt-4 flex flex-col space-y-2 sm:space-y-3 border border-white/70 ring-1 ring-[#0C4B45]/10"
            variants={menuVariants}
          >
            <MobileItem to="/" active={location.pathname === '/'} onClick={() => setIsOpen(false)} variants={itemVariants}>
              <FaHome className="mr-3" /> Inicio
            </MobileItem>

            <MobileItem to="/products" active={location.pathname.startsWith('/products') || location.pathname.startsWith('/product/')} onClick={() => setIsOpen(false)} variants={itemVariants}>
              <FaStore className="mr-3" /> Productos
            </MobileItem>

            <MobileItem to="/cart" active={location.pathname === '/cart'} onClick={() => setIsOpen(false)} variants={itemVariants}>
              <FaShoppingCart className="mr-3" /> Carrito {itemCount > 0 && (
                <span className="ml-auto bg-[#662D8F] text-white text-xs rounded-full h-5 w-5 sm:h-6 sm:w-6 flex items-center justify-center">
                  {itemCount}
                </span>
              )}
            </MobileItem>

            {user ? (
              <>
                <MobileItem to="/profile" active={location.pathname === '/profile'} onClick={() => setIsOpen(false)} variants={itemVariants}>
                  <FaUser className="mr-3" /> Mi Perfil
                </MobileItem>
                
                <motion.button
                  onClick={() => {
                    handleLogout();
                    setIsOpen(false);
                  }}
                  className="flex items-center w-full px-5 py-3 rounded-lg font-medium text-sm text-red-600 hover:bg-red-50 transition-colors duration-300"
                  variants={itemVariants}
                >
                  <FaSignOutAlt className="mr-3" />
                  Cerrar sesión
                </motion.button>
              </>
            ) : (
              <MobileItem to="/login" active={location.pathname === '/login'} onClick={() => setIsOpen(false)} variants={itemVariants}>
                <FaUser className="mr-3" /> Iniciar Sesión
              </MobileItem>
            )}
          </motion.div>
        </motion.div>
      </div>
    </nav>
  );
};

// Componentes reutilizables
const NavLink = ({ to, children, active, scrolled }) => (
  <motion.div whileHover={{ y: -2 }} className="relative">
    <Link
      to={to}
      className={`flex items-center px-3 py-2.5 rounded-xl font-medium text-sm transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#83F4E9] focus-visible:ring-offset-2 ${
        active 
          ? (scrolled ? 'text-white bg-gradient-to-r from-[#662D8F] to-[#512577] shadow-md' : 'text-white bg-gradient-to-r from-[#662D8F] to-[#512577] shadow-md') 
          : (scrolled ? 'text-[#0C4B45] hover:text-[#662D8F] hover:bg-gray-50' : 'text-white/90 hover:text-white hover:bg-white/10 backdrop-blur-sm')
      }`}
    >
      {children}
    </Link>
  </motion.div>
);

const CartLink = ({ itemCount, scrolled, isMobile = false }) => (
  <motion.div 
    whileHover={isMobile ? {} : { y: -2 }} 
    whileTap={isMobile ? { scale: 0.95 } : {}}
    className="relative"
  >
    <Link 
      to="/cart" 
      className={`flex items-center ${isMobile ? 'p-2.5 rounded-xl shadow-md' : 'px-3 py-2.5 rounded-xl'} ${
        scrolled && isMobile 
          ? 'bg-[#662D8F] text-white hover:bg-[#512577]' 
          : isMobile 
            ? 'bg-white/20 text-white backdrop-blur-sm hover:bg-white/30' 
            : scrolled 
              ? 'text-[#0C4B45] hover:text-[#662D8F] hover:bg-gray-50' 
              : 'text-white/90 hover:text-white hover:bg-white/10 backdrop-blur-sm'
      } transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#83F4E9] focus-visible:ring-offset-2`}
    >
      <FaShoppingCart className={`${isMobile ? 'text-lg' : 'text-lg mr-1'}`} />
      {!isMobile && "Carrito"}
      {itemCount > 0 && (
        <span className={`${isMobile ? 'absolute -top-1 -right-1' : 'ml-1'} text-xs rounded-full h-5 w-5 flex items-center justify-center ${
          scrolled && !isMobile 
            ? 'bg-[#662D8F] text-white' 
            : isMobile 
              ? 'bg-white text-[#662D8F]' 
              : 'bg-white text-[#662D8F]'
        } font-bold`}>
          {itemCount}
        </span>
      )}
    </Link>
  </motion.div>
);

const MobileItem = ({ to, children, active, onClick, variants }) => (
  <motion.div variants={variants}>
    <Link
      to={to}
      onClick={onClick}
      className={`flex items-center px-5 py-3 rounded-lg font-medium text-sm transition-colors duration-300 ${
        active 
          ? 'bg-gradient-to-r from-[#662D8F] to-[#512577] text-white' 
          : 'text-[#0C4B45] hover:bg-[#83F4E9]/30'
      }`}
    >
      {children}
    </Link>
  </motion.div>
);

export default Navbar;
